// Svelte-Action: haengt ein Dropdown an <body> und positioniert es
// `fixed` unter seinem Anker (Trigger-Button bzw. -Feld in der TopBar).
//
// Warum nicht einfach `absolute top-full` im Header, wie es frueher war:
// die TopBar-Pille ist ein `sticky`, `z-30`-Element. Safari promotet
// sticky Elemente auf eine eigene Compositing-Ebene und beschneidet
// deren ueberstehende Nachfahren an den Grenzen dieser Ebene. Vom
// Such-Dropdown blieb dadurch nur ein Streifen in Hoehe der Pille
// deckend -- Hintergrund, Rahmen und das `overflow-hidden`-Clipping
// endeten dort, waehrend die Trefferliste ungeclippt und durchsichtig
// ueber die Seite weiterlief (Safari, 26.07.; Chromium zeigt es nicht).
// Am <body> gibt es weder einen sticky- noch einen maskierten Vorfahren
// (s. portal.ts zur `content-top-fade`-Maske auf <main>), damit ist die
// Mal-Reihenfolge eindeutig.
//
// Zweiter Gewinn: die maximale Hoehe kommt aus dem ECHTEN Platz unter
// dem Anker statt aus einer `calc(100dvh - 8rem)`-Schaetzung, die auf
// jeder anderen Bar-Hoehe daneben liegt.
//
// Dritter Gewinn (placement:"auto", seit 02.09.): Anker MITTEN in der
// Seite -- Zeilenmenues in Tabellen, Aktionsmenues auf Karten -- haben
// unter sich oft keinen Platz mehr. Die TopBar-Popover haengen immer
// oben und klappen darum grundsaetzlich nach unten (Default "below");
// ein Zeilenmenue in der letzten Tabellenzeile klappt nach oben.

import { bereichMitnehmen } from "./portal";

export type PopoverAlign = "left" | "right";

/** "below" = immer unter den Anker (TopBar). "auto" = nach oben klappen,
 *  wenn unten der Platz nicht reicht und oben mehr ist (Zeilenmenues). */
export type PopoverPlacement = "below" | "auto";

export interface AnchoredPopoverOptions {
  /** Element, unter dem das Panel haengt. Ohne Anker (und ohne punkt)
   *  passiert nichts. */
  anchor?: HTMLElement | null;
  /** Statt eines Elements ein Punkt im Fenster (clientX/clientY) --
   *  fuer Kontextmenues am Mauszeiger. Gewinnt vor anchor. */
  punkt?: { x: number; y: number } | null;
  /** Welche Kante buendig mit dem Anker liegt (Default: links). */
  align?: PopoverAlign;
  /** Abstand zwischen Anker-Unterkante und Panel (px, Default 8 = mt-2). */
  gap?: number;
  /** Mindestabstand zu den Viewport-Raendern (px, Default 12). */
  margin?: number;
  /** Klapprichtung (Default "below"). */
  placement?: PopoverPlacement;
}

export function anchoredPopover(
  node: HTMLElement,
  options: AnchoredPopoverOptions,
) {
  let opts = options;

  // Wie portal: die Bereichsfarbe der Seite geht mit an <body>.
  bereichMitnehmen(node);
  document.body.appendChild(node);
  node.style.position = "fixed";

  // Rechteck des Ankers. Ein punkt ist ein Rechteck ohne Ausdehnung:
  // "unter dem Anker" heisst dann "ab dem Zeiger", "nach oben
  // geklappt" endet das Panel am Zeiger (Repo-Prüfung 21.10.2026,
  // zweite Runde, Oberflächen-Befund 7: das Kontextmenue der Dateien
  // lief am rechten und unteren Rand aus dem Bild).
  function ankerRechteck(): { top: number; bottom: number; left: number; right: number } | null {
    if (opts.punkt) {
      const { x, y } = opts.punkt;
      return { top: y, bottom: y, left: x, right: x };
    }
    const anchor = opts.anchor;
    if (!anchor || !anchor.isConnected) return null;
    return anchor.getBoundingClientRect();
  }

  function place() {
    const r = ankerRechteck();
    if (!r) return;

    const gap = opts.gap ?? 8;
    const margin = opts.margin ?? 12;
    // visualViewport ist auf iOS die verlaessliche Quelle (aufgezogene
    // Tastatur / eingeklappte URL-Leiste), Fallback fuer alles andere.
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;

    const platzUnten = vh - r.bottom - gap - margin;
    const platzOben = r.top - gap - margin;

    // Fuer "auto" die NATUERLICHE Hoehe messen -- ohne den Deckel des
    // letzten Laufs, sonst misst man seine eigene Vorgabe von vorhin.
    let nachOben = false;
    if (opts.placement === "auto") {
      node.style.maxHeight = "";
      nachOben = node.offsetHeight > platzUnten && platzOben > platzUnten;
    }

    // Hoehe ZUERST setzen: sie entscheidet, ob eine Scrollbar erscheint,
    // und damit ueber die Breite, die wir gleich messen.
    if (nachOben) {
      node.style.maxHeight = `${Math.max(120, Math.round(platzOben))}px`;
      // Oberkante aus der GEDECKELTEN Hoehe: bei kurzem Panel steht es
      // direkt ueber dem Anker, bei langem am oberen Rand.
      node.style.top = `${Math.round(Math.max(margin, r.top - gap - node.offsetHeight))}px`;
    } else {
      const top = r.bottom + gap;
      node.style.top = `${Math.round(top)}px`;
      node.style.maxHeight = `${Math.max(120, Math.round(vh - top - margin))}px`;
    }

    const w = node.offsetWidth;
    const raw = opts.align === "right" ? r.right - w : r.left;
    const left = Math.min(Math.max(raw, margin), Math.max(margin, vw - margin - w));
    node.style.left = `${Math.round(left)}px`;
  }

  place();
  // Nach dem ersten Layout-Frame nachziehen: beim Mount steht die
  // Inhaltsbreite (und damit offsetWidth) noch nicht fest.
  const raf = requestAnimationFrame(place);

  // scroll mit capture: der Anker haengt an der schwebenden TopBar, aber
  // Trigger anderer Popover koennen mit dem Inhalt in <main> wandern.
  window.addEventListener("resize", place);
  window.addEventListener("scroll", place, true);
  window.visualViewport?.addEventListener("resize", place);
  window.visualViewport?.addEventListener("scroll", place);

  // Panel wird hoeher/breiter (Treffer trudeln asynchron ein), Anker
  // aendert seine Groesse (Breadcrumb-Label wechselt).
  const ro = new ResizeObserver(place);
  ro.observe(node);
  if (options.anchor) ro.observe(options.anchor);

  return {
    update(next: AnchoredPopoverOptions) {
      if (opts.anchor && opts.anchor !== next.anchor) ro.unobserve(opts.anchor);
      if (next.anchor && next.anchor !== opts.anchor) ro.observe(next.anchor);
      opts = next;
      place();
    },
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
      node.remove();
    },
  };
}
